import React, { ComponentProps, ReactText, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogProps,
  DialogTitle,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import styled, { StyledComponentPropsWithRef } from 'styled-components';

import { SPWCancelButton, SPWSaveButton } from './buttons';
import { Row } from '.';

export const SPWBaseDialog = ({
  cancelButtonProps = {},
  disableScrollLock = true,
  children,
  noCancel = false,
  onClose,
  saveButtonProps = {},
  ...dialogProps
}: {
  cancelButtonProps?: StyledComponentPropsWithRef<typeof SPWCancelButton>;
  noCancel?: boolean;
  saveButtonProps?: StyledComponentPropsWithRef<typeof SPWSaveButton>;
} & DialogProps) => {
  const handleClose = useCallback<DialogProps['onClose']>(
    (_, reason) => {
      if (reason !== 'backdropClick' && onClose) onClose(_, reason);
    },
    [onClose]
  );

  return (
    <Dialog
      {...dialogProps}
      onClose={handleClose}
      disableScrollLock={disableScrollLock}
    >
      {children}
      <SPWDialogActions>
        <SPWSaveButton {...saveButtonProps} />
        {!noCancel && <SPWCancelButton {...cancelButtonProps} />}
      </SPWDialogActions>
    </Dialog>
  );
};

const useSPWDialogWithTitleStyles = makeStyles({
  dialogTitle: {
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '24px',
    padding: '24px',
  },
  dialogContent: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    padding: '0 24px',
  },
});

type SPWDialogWithTitleProps = ComponentProps<typeof SPWBaseDialog>;

export const SPWDialogWithTitle = ({
  children,
  title,
  ...spwBaseDialogProps
}: SPWDialogWithTitleProps) => {
  const classes = useSPWDialogWithTitleStyles();

  return (
    <SPWBaseDialog {...spwBaseDialogProps}>
      <DialogTitle className={classes.dialogTitle}>{title}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {children}
      </DialogContent>
    </SPWBaseDialog>
  );
};

const SPWFormDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 446px;
  overflow-y: auto;
  box-sizing: border-box;

  .title {
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    margin: 24px 24px 8px 24px;
  }
  .content {
    overflow-y: auto;
    padding: 0 24px;
  }
  .description {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 24px;
  }
  .directions {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 8px;
  }
  .expanded {
    flex: 1;
  }
  .footer {
    margin: 16px 24px 24px 24px;

    .MuiButton-root {
      box-sizing: border-box;
      width: 88px;
    }
  }
  .margin-right-16 {
    margin-right: 16px;
  }
  .margin-bottom-24 {
    margin-bottom: 24px;
  }
`;

type SPWFormDialog = DialogProps & {
  className?: string;
  dialogActions: JSX.Element;
  dialogDescription?: ReactText | JSX.Element;
  dialogTitle: ReactText | JSX.Element;
  baseDialogClassName?: string;
};

export const SPWFormDialog = ({
  children,
  className = '',
  dialogActions,
  dialogDescription,
  dialogTitle,
  onClose,
  baseDialogClassName,
  ...dialogProps
}: SPWFormDialog) => {
  const handleClose = useCallback<DialogProps['onClose']>(
    (_, reason) => {
      if (reason !== 'backdropClick' && onClose) onClose(_, reason);
    },
    [onClose]
  );

  return (
    <Dialog
      {...dialogProps}
      className={baseDialogClassName}
      onClose={handleClose}
    >
      <SPWFormDialogContainer className={className}>
        <div className="expanded">
          <div className="title">{dialogTitle}</div>
          <div className="content">
            {dialogDescription && (
              <div className="description">{dialogDescription}</div>
            )}
            {children}
          </div>
        </div>
        <Row className="footer">{dialogActions}</Row>
      </SPWFormDialogContainer>
    </Dialog>
  );
};

export const SPWDialogActions = styled((props: DialogActionsProps) => (
  <DialogActions {...props} className={`SpwDialogActions ${props.className}`}>
    {props.children}
  </DialogActions>
))`
  && {
    justify-content: flex-start;
    padding: 24px 24px;

    &.small-bottom-padding {
      padding-bottom: 16px;
    }
  }
  display: flex;
`;
